import { createTheme } from '@mui/material/styles';
import vars from 'assets/scss/_themes-vars.module.scss';

const palette = {
  mode: 'light',
  primary: {
    light: vars.primaryLight,
    main: vars.primaryMain,
    dark: vars.primaryDark,
    200: vars.primary200,
    800: vars.primary800,
  },
  secondary: {
    light: vars.greyLight,
    main: vars.greyMain,
  },
  purple: {
    light: vars.purpleLight,
    main: vars.purpleMain,
  },
  success: {
    light: vars.secondaryLight,
    main: vars.secondaryMain,
    dark: vars.secondaryDark,
    200: vars.secondary200,
    800: vars.secondary800,
  },
  info: {
    light: vars.infoLight,
    main: vars.infoMain,
    dark: vars.infoDark,
    200: vars.info200,
    800: vars.info800,
  },
  warning: {
    light: vars.warningLight,
    main: vars.warningMain,
    dark: vars.warningDark,
  },
  grey: {
    light: vars.greyLight,
    main: vars.greyMain,
  },
  dark: {
    light: vars.darkTextPrimary,
    main: vars.darkLevel1,
    default: vars.darkLevel2,
    800: vars.darkBackground,
    900: vars.darkPaper,
  },
  background: {
    paper: vars.paper,
    default: vars.paper,
    primary: vars.primaryLight,
  },
};

const breakpoints = {
  ...createTheme().breakpoints,
  values: {
    sm: vars.sm,
    ms: vars.ms,
    md: vars.md,
    ml: vars.ml,
    lg: vars.lg,
    xl: vars.xl,
    xxl: vars.xxl,
    xxxl: vars.xxxl,
    hg: vars.hg,
    eh: vars.eh,
    mg: vars.mg,
    ul: vars.ul,
  },
};

const typography = {
  fontFamily: 'bahnschrift',
  h6: {
    fontWeight: 500,
    color: vars.darkLevel2,
    fontSize: '0.75rem',
  },
  h5: {
    fontSize: '0.875rem',
    color: vars.darkLevel2,
    fontWeight: 500,
  },
  h4: {
    fontSize: '1rem',
    color: vars.darkLevel2,
    fontWeight: 500,
  },
  h3: {
    fontSize: '1.25rem',
    color: vars.darkLevel2,
    fontWeight: 600,
  },
  h2: {
    fontSize: '1.5rem',
    color: vars.darkLevel2,
    fontWeight: 700,
  },
  h1: {
    fontSize: '2.125rem',
    color: vars.darkLevel2,
    fontWeight: 600,
    '@media (min-width:600px)': {
      fontSize: '1.5rem',
    },
    [breakpoints.up('md')]: {
      fontSize: '2.4rem',
    },
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    color: vars.greyMain,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: vars.greyMain,
  },
  caption: {
    fontSize: '0.875rem',
    color: vars.darkLevel1,
    fontWeight: 400,
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: '1.5em',
    color: vars.darkLevel2,
  },
  body2: {
    fontSize: '1rem',
    letterSpacing: '0em',
    fontWeight: 400,
    lineHeight: '1.32em',
    color: vars.darkLevel2,
  },
};

const components = {
  MuiPaper: {
    styleOverrides: {
      root: {
        border: '1px solid',
        borderColor: vars.greyLight,
        padding: '10px',
        boxShadow: 'none',
        borderRadius: '5px',
      },
    },
  },
  MuiButton: {
    variants: [
      {
        props: { variant: 'text' },
        style: {
          textTransform: 'capitalize',
        },
      },
      {
        props: { variant: 'contained' },
        style: {
          textTransform: 'capitalize',
        },
      },
      {
        props: { variant: 'outlined' },
        style: {
          textTransform: 'capitalize',
        },
      },
    ],
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        '&.Mui-focused': {
          color: vars.darkLevel1,
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      root: {
        '& .MuiPaper-root': {
          borderRadius: 5,
          border: `1px solid ${vars.greyLight}`,
          boxShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        },
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        paddingTop: '10px',
        paddingBottom: '10px',
        '&.Mui-selected': {
          color: vars.primary200,
          backgroundColor: vars.primaryMain,
          '&:hover': {
            backgroundColor: vars.primaryDark,
          },
          '& .MuiListItemIcon-root': {
            color: vars.primary200,
          },
        },
        '&:hover': {
          backgroundColor: vars.primaryLight,
        },
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: '40px',
      },
    },
  },
};

const MainTheme = createTheme({
  palette,
  breakpoints,
  typography,
  components,
});

export default MainTheme;